/* Collection destinations are indexed separately from product fitment. */
(() => {
  const fold = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const compact = (value) => fold(value).replace(/[^a-z0-9]/g, '');
  const atoms = (value) => fold(value).match(/[a-z]+|\d+/g) || [];
  const normalize = (value) => globalThis.RicochetSearchNormalizer?.normalizeSearchQuery?.(value)
    || globalThis.RicochetSearchNormalizer?.normalizeQuery(value) || value;
  const oneEdit = (left, right) => {
    if (Math.abs(left.length - right.length) > 1) return false;
    if (left.length === right.length) {
      const differences = [...left].map((letter, i) => letter === right[i] ? -1 : i).filter((i) => i >= 0);
      return differences.length === 1 || (differences.length === 2 && differences[1] === differences[0] + 1
        && left[differences[0]] === right[differences[1]] && left[differences[1]] === right[differences[0]]);
    }
    const [short, long] = left.length < right.length ? [left, right] : [right, left];
    return [...long].some((_, i) => long.slice(0, i) + long.slice(i + 1) === short);
  };
  const isProductQuery = (query) => {
    const data = globalThis.RicochetSearchData || {};
    const key = compact(query);
    if ((data.skus || []).includes(key)) return true;
    const normalized = normalize(query);
    const text = atoms(globalThis.RicochetSearchNormalizer?.normalizeProductQuery?.(normalized) || normalized).join(' ');
    return (data.productTypes || []).some((type) => {
      const phrase = atoms(type).join(' ');
      return phrase && (` ${text} `).includes(` ${phrase} `);
    });
  };
  const score = (collection, query) => {
    const tokens = atoms(normalize(query));
    if (!tokens.length || !/^[a-z0-9_-]+$/i.test(collection.h || '')) return 0;
    // Handles are routing identifiers, not reliable aliases after a collection rename.
    // Use one only when there is no authored identity to search.
    const labels = [collection.t, collection.mk, collection.md, collection.vt,
      collection.family, collection.variant].filter((label) => String(label || '').trim());
    if (!labels.length) labels.push(collection.h.replace(/-/g, ' '));
    const candidates = [...new Set(labels.flatMap(atoms))];
    const suffixes = labels.flatMap((label) => (fold(label).match(/[a-z0-9]+/g) || [])
      .map((word) => word.match(/(\d+)([a-z]{1,3})$/)).filter(Boolean));
    let penalty = 0;
    for (const [index, token] of tokens.entries()) {
      const previous = tokens[index - 1];
      const codes = suffixes.filter((code) => previous && code[1].startsWith(previous));
      if (codes.length && token.length <= 3 && /^[a-z]+$/.test(token)) {
        if (!codes.some((code) => code[1] === previous && code[2] === token)) return 0;
        continue;
      }
      if (candidates.includes(token)) continue;
      // Prefixes are browsing candidates, never a numeric correction or auto-selection.
      if (candidates.some((candidate) => candidate.startsWith(token)
          && (/^\d+$/.test(token) ? /^\d+$/.test(candidate)
            : token.length >= 2 && !suffixes.some((code) => code[2] === candidate)))) {
        penalty += 5;
        continue;
      }
      // Unknown short codes/suffixes and numeric neighbors are not typos.
      if (/\d/.test(token) || token.length < 4) return 0;
      if (candidates.some((candidate) => candidate.length >= 4 && !/\d/.test(candidate) && oneEdit(token, candidate))) {
        penalty += 10;
      } else return 0;
    }
    const key = compact(normalize(query));
    return (labels.some((label) => compact(label) === key) ? 100 : 90) - penalty;
  };
  const rank = (collections, query) => collections.map((document) => ({ document, confidence: score(document, query) }))
    .filter((match) => match.confidence > 0)
    .sort((left, right) => right.confidence - left.confidence
      || String(left.document.t || left.document.h).length - String(right.document.t || right.document.h).length
      || String(left.document.t || left.document.h).localeCompare(String(right.document.t || right.document.h)));
  globalThis.RicochetCollectionSearch = { rank, score, isProductQuery };
})();
